import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Divider,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { liveURL } from '../constants/Consts';

const cities = [
  { name: "Islamabad", value: "Islamabad" },
  { name: "Lahore", value: "Lahore" },
  { name: "Multan", value: "Multan" },
  { name: "Karachi", value: "Karachi" },
  { name: "Quetta", value: "Quetta" },
  { name: "Peshawar", value: "Peshawar" },
];

export default function NamazTimings() {
  const { userData } = useAuth();
  const [selectedCity, setSelectedCity] = useState('');
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [addedCities, setAddedCities] = useState([]);
  const [fetchingCities, setFetchingCities] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const fetchAddedCities = useCallback(async () => {
    setFetchingCities(true);
    try {
      const response = await axios.get(`${liveURL}/v1/adminNamazTimings/added-cities`, {
        headers: {
          'Authorization': `Bearer ${userData?.token}`
        }
      });
      if (response.data.status === 200) {
        setAddedCities(response.data.data);
      }
    } catch (err) {
      console.error("Error fetching cities:", err);
    } finally {
      setFetchingCities(false);
    }
  }, [userData?.token]);

  useEffect(() => {
    fetchAddedCities();
  }, [fetchAddedCities]);

  const handleCityChange = (e) => {
    setSelectedCity(e.target.value);
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      setFile(e.target.files[0]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedCity) {
      setMessage({ type: 'error', text: 'Please select a city.' });
      return;
    }
    if (!file) {
      setMessage({ type: 'error', text: 'Please upload an Excel file.' });
      return;
    }

    const formData = new FormData();
    formData.append('city', selectedCity);
    formData.append('file', file);

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await axios.post(`${liveURL}/v1/adminNamazTimings/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${userData?.token}`
        }
      });

      if (response.data.status === 200) {
        setMessage({ type: 'success', text: response.data.message });
        setFile(null); // clear file input
        document.getElementById('excel-upload').value = '';
        fetchAddedCities(); // Refresh list
      } else {
        setMessage({ type: 'error', text: 'Error uploading timings.' });
      }
    } catch (err) {
        console.error(err);
        setMessage({ type: 'error', text: err.response?.data?.message || 'An error occurred during upload.' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (city) => {
    if (!window.confirm(`Are you sure you want to delete ALL namaz timings for ${city}?`)) {
      return;
    }

    try {
      const response = await axios.delete(`${liveURL}/v1/adminNamazTimings/delete-city/${city}`, {
        headers: {
          'Authorization': `Bearer ${userData?.token}`
        }
      });
      if (response.data.status === 200) {
        setMessage({ type: 'success', text: response.data.message });
        fetchAddedCities(); // Refresh list
      }
    } catch (err) {
      console.error("Delete failed:", err);
      setMessage({ type: 'error', text: 'Failed to delete city timings.' });
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Namaz Timings Management
      </Typography>
      
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4 }}>
        {/* Upload Section */}
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" gutterBottom>Upload New Timings</Typography>
          <Paper sx={{ p: 4 }}>
            {message.text && (
              <Alert severity={message.type} sx={{ mb: 3 }} onClose={() => setMessage({ type: '', text: '' })}>
                {message.text}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel id="city-select-label">City</InputLabel>
                <Select
                  labelId="city-select-label"
                  value={selectedCity}
                  label="City"
                  onChange={handleCityChange}
                >
                  <MenuItem value=""><em>Select City</em></MenuItem>
                  {cities.map((city) => (
                    <MenuItem key={city.value} value={city.value}>
                      {city.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Upload Excel File (.xlsx)
                </Typography>
                <input
                  id="excel-upload"
                  type="file"
                  accept=".xlsx, .xls"
                  onChange={handleFileChange}
                  style={{ width: '100%', padding: '10px 0' }}
                />
              </Box>

              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
              >
                {loading ? 'Uploading...' : 'Upload Timings'}
              </Button>
            </form>
          </Paper>
        </Box>

        {/* List Section */}
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" gutterBottom>Cities with Timings</Typography>
          <Paper sx={{ p: 2 }}>
            {fetchingCities ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress size={24} />
              </Box>
            ) : addedCities.length === 0 ? (
              <Typography variant="body2" sx={{ p: 2, textAlign: 'center' }}>No cities added yet.</Typography>
            ) : (
              <List>
                {addedCities.map((city, index) => (
                  <React.Fragment key={city}>
                    {index > 0 && <Divider />}
                    <ListItem
                      secondaryAction={
                        <IconButton edge="end" aria-label="delete" color="error" onClick={() => handleDelete(city)}>
                          <DeleteIcon />
                        </IconButton>
                      }
                    >
                      <ListItemText primary={city} />
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
            )}
          </Paper>
        </Box>
      </Box>
    </Box>
  );
}

